import { reportLog } from 'services/userMonitoring'

/**
 * Represents a traveller detail for insurance validation.
 * firstname and surname are optional to handle cases where
 * the data might not be filled in the form yet.
 */
export interface InsuranceTravellerDetail {
  firstname: string | undefined
  surname: string | undefined
  age_range: string
}

export interface InsuranceTravellerMismatchDetails {
  quoteId: string
  productId: string
  insuranceType: string
  /** The response's traveller_count — excludes infants; kept for dashboard continuity. */
  quoteTravellerCount: number
  /** The count the order is validated against: every traveller the quote was requested for. */
  quoteExpectedTravellerCount: number
  quoteRequestedTravellers?: App.InsuranceQuote['requestedTravellers']
  actualTravellerCount: number
  difference: number
  quoteKey?: string
  itemId: string
  travellersDetails: Array<InsuranceTravellerDetail>
  isUsingCustomTravellers: boolean
  isCruiseCheckout: boolean
}

export interface InsuranceTravellerCompositionMismatchDetails {
  quoteId: string
  productId: string
  insuranceType: string
  itemId: string
  quoteKey?: string
  quoteRequestedTravellers: NonNullable<App.InsuranceQuote['requestedTravellers']>
  travellersByAgeRange: Record<string, number>
  travellersDetailsCount: number
  isUsingCustomTravellers: boolean
  isCruiseCheckout: boolean
}

export interface InsuranceTravellerValidationResult {
  /** Count validity only — a composition-only drift still reports isValid true. */
  isValid: boolean
  mismatch?: InsuranceTravellerMismatchDetails
  /**
   * Set when the totals match but the per-age-group composition differs from
   * what the quote was requested for. Log-only signal: the order still submits.
   */
  compositionMismatch?: InsuranceTravellerCompositionMismatchDetails
}

function countTravellersByAgeRange(travellersDetails: Array<InsuranceTravellerDetail>): Record<string, number> {
  return travellersDetails.reduce<Record<string, number>>((counts, traveller) => {
    const ageRange = traveller.age_range || 'unknown'
    counts[ageRange] = (counts[ageRange] ?? 0) + 1
    return counts
  }, {})
}

/**
 * Seniors are folded into adults on both sides: the frontend submits quoted
 * seniors as adults and svc-insurance converts them back at create time, so
 * adult/senior labels are interchangeable while child/infant are not.
 */
function matchesQuotedComposition(
  requestedTravellers: NonNullable<App.InsuranceQuote['requestedTravellers']>,
  travellersByAgeRange: Record<string, number>,
): boolean {
  const submittedAdults = (travellersByAgeRange.adult ?? 0) + (travellersByAgeRange.senior ?? 0)
  const submittedChildren = travellersByAgeRange.child ?? 0
  const submittedInfants = travellersByAgeRange.infant ?? 0

  return (
    submittedAdults === requestedTravellers.adults + requestedTravellers.seniors &&
    submittedChildren === requestedTravellers.children &&
    submittedInfants === requestedTravellers.infants
  )
}

/**
 * Validates that the number of travellers in the insurance quote matches
 * the number of travellers being submitted in the order.
 *
 * This is a critical validation to prevent XCover rejections due to
 * traveller count mismatches between quote fetching and order submission.
 */
export function validateInsuranceTravellerCount(
  quote: App.InsuranceQuote | undefined,
  travellersDetails: Array<InsuranceTravellerDetail>,
  itemId: string,
  insuranceType: string,
  isUsingCustomTravellers: boolean,
  isCruiseCheckout: boolean,
): InsuranceTravellerValidationResult {
  if (!quote) {
    return { isValid: true } // No quote means no validation needed
  }

  /**
   * The quote is validated against the travellers it was REQUESTED for: the
   * response's traveller_count is adults+children+seniors and leaves infants
   * out, so a cart with infant forms would always read as an over-submission.
   * Quotes stored before requestedTravellers existed fall back to the response count.
   */
  const requestedTravellers = quote.requestedTravellers
  const quoteExpectedTravellerCount = requestedTravellers
    ? requestedTravellers.adults + requestedTravellers.children + requestedTravellers.infants + requestedTravellers.seniors
    : quote.travellerCount
  const actualTravellerCount = travellersDetails.length

  if (quoteExpectedTravellerCount === actualTravellerCount) {
    if (requestedTravellers && travellersDetails.length > 0) {
      const travellersByAgeRange = countTravellersByAgeRange(travellersDetails)
      if (!matchesQuotedComposition(requestedTravellers, travellersByAgeRange)) {
        return {
          isValid: true,
          compositionMismatch: {
            quoteId: quote.id,
            productId: quote.productId,
            insuranceType,
            itemId,
            quoteKey: quote.key,
            quoteRequestedTravellers: requestedTravellers,
            travellersByAgeRange,
            travellersDetailsCount: travellersDetails.length,
            isUsingCustomTravellers,
            isCruiseCheckout,
          },
        }
      }
    }
    return { isValid: true }
  }

  const mismatchDetails: InsuranceTravellerMismatchDetails = {
    quoteId: quote.id,
    productId: quote.productId,
    insuranceType,
    quoteTravellerCount: quote.travellerCount,
    quoteExpectedTravellerCount,
    quoteRequestedTravellers: requestedTravellers,
    actualTravellerCount,
    difference: actualTravellerCount - quoteExpectedTravellerCount,
    quoteKey: quote.key,
    itemId,
    travellersDetails,
    isUsingCustomTravellers,
    isCruiseCheckout,
  }

  return {
    isValid: false,
    mismatch: mismatchDetails,
  }
}

/**
 * Whether a validation result is a genuine over-submission — the one mismatch
 * direction that fails at the provider: it receives the insured list verbatim
 * and rejects a longer-than-quoted list with a 4xx AFTER payment, which
 * auto-cancels and refunds the paid item. Under-submission is benign — the
 * confirm step pads the insured list up to the quoted count, and multi-room
 * carts rely on quoting more travellers than they submit.
 *
 * Requires the quote to carry requestedTravellers: the legacy travellerCount
 * fallback excludes infants, so without it any cart holding an infant would
 * read as an over-submission spuriously.
 */
export function isInsuranceTravellerOverSubmission(result: InsuranceTravellerValidationResult): boolean {
  return !result.isValid && !!result.mismatch?.quoteRequestedTravellers && result.mismatch.difference > 0
}

/**
 * Builds the structured log payload shared by the traveller mismatch loggers.
 */
function buildMismatchLogData(
  mismatch: InsuranceTravellerMismatchDetails,
  additionalContext?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    // Core mismatch information
    quoteId: mismatch.quoteId,
    productId: mismatch.productId,
    insuranceType: mismatch.insuranceType,
    quoteTravellerCount: mismatch.quoteTravellerCount,
    quoteExpectedTravellerCount: mismatch.quoteExpectedTravellerCount,
    quoteRequestedTravellers: mismatch.quoteRequestedTravellers,
    actualTravellerCount: mismatch.actualTravellerCount,
    difference: mismatch.difference,
    mismatchDirection: mismatch.difference > 0 ? 'order_has_more' : 'order_has_fewer',

    // Context for debugging
    quoteKey: mismatch.quoteKey,
    itemId: mismatch.itemId,
    isUsingCustomTravellers: mismatch.isUsingCustomTravellers,
    isCruiseCheckout: mismatch.isCruiseCheckout,

    // Traveller details for investigation
    travellersDetailsCount: mismatch.travellersDetails.length,
    travellersAgeRanges: mismatch.travellersDetails.map(t => t.age_range).join(','),
    travellersByAgeRange: countTravellersByAgeRange(mismatch.travellersDetails),

    // Additional context if provided
    ...additionalContext,
  }
}

/**
 * Logs insurance traveller count mismatch to monitoring with detailed information
 * for debugging and tracking purposes.
 *
 * This log will help identify:
 * - Which checkout flows are causing mismatches
 * - The specific discrepancy between quote and order
 * - Whether custom travellers feature is involved
 */
export function logInsuranceTravellerMismatch(
  mismatch: InsuranceTravellerMismatchDetails,
  additionalContext?: Record<string, unknown>,
): void {
  reportLog(
    `Insurance traveller count mismatch: quote expects ${mismatch.quoteExpectedTravellerCount} travellers, order has ${mismatch.actualTravellerCount}`,
    buildMismatchLogData(mismatch, additionalContext),
    'warn',
  )
}

const reportedOverSubmissionKeys = new Set<string>()

/**
 * Logs a genuine over-submission. Distinct from the warn-level mismatch log
 * (which fires for both directions) and intended as an alerting signal: any
 * hit here means an insurance item is being submitted with more travellers
 * than its quote was priced for, which the provider will reject after payment
 * (auto-cancel + refund). It points at a live occupancy/traveller divergence
 * bug needing manual analysis.
 *
 * The message is static so RUM error tracking fingerprints every occurrence
 * as one issue — the counts live in the log context. Callers run inside
 * selector recomputes, so each item + quote state reports only once per page
 * lifecycle; a refetched quote (new key) re-arms the signal.
 */
export function logInsuranceTravellerOverSubmission(
  mismatch: InsuranceTravellerMismatchDetails,
  additionalContext?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const dedupeKey = `${mismatch.itemId}:${mismatch.quoteKey ?? mismatch.quoteId}`
  if (reportedOverSubmissionKeys.has(dedupeKey)) {
    return
  }
  reportedOverSubmissionKeys.add(dedupeKey)

  reportLog(
    'Insurance traveller over-submission detected: order submits more travellers than the quote was priced for',
    buildMismatchLogData(mismatch, additionalContext),
    'error',
  )
}

/**
 * Logs a composition-only drift: the order submits the right NUMBER of
 * travellers but their age groups differ from what the quote was requested
 * (and priced) for. The order-side traveller allocation should make this
 * impossible for form-based items, so any hit points at a regression or at a
 * custom-traveller set diverging from its quote.
 */
export function logInsuranceTravellerCompositionMismatch(
  compositionMismatch: InsuranceTravellerCompositionMismatchDetails,
  additionalContext?: Record<string, unknown>,
): void {
  reportLog(
    'Insurance traveller composition mismatch: order age groups differ from the quoted composition',
    {
      quoteId: compositionMismatch.quoteId,
      productId: compositionMismatch.productId,
      insuranceType: compositionMismatch.insuranceType,
      itemId: compositionMismatch.itemId,
      quoteKey: compositionMismatch.quoteKey,
      quoteRequestedTravellers: compositionMismatch.quoteRequestedTravellers,
      travellersByAgeRange: compositionMismatch.travellersByAgeRange,
      travellersDetailsCount: compositionMismatch.travellersDetailsCount,
      isUsingCustomTravellers: compositionMismatch.isUsingCustomTravellers,
      isCruiseCheckout: compositionMismatch.isCruiseCheckout,
      ...additionalContext,
    },
    'warn',
  )
}
